import ModelFactory from 'business/models/ModelFactory';
import { LicenseClient } from 'business/models/Models';
import useLogoutUser from 'CustomHooks/useLogoutUser';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  FmlxBanner,
  FmlxButton,
  FmlxModal,
  FmlxRadioButton,
  FmlxTextBox,
  FmlxTextInput,
} from 'fmlx-common-ui';
import React, { useEffect, useRef, useState } from 'react';
import { showAlertSnackBar, showSnackBar } from 'utils/Global';
import { DASHBOARD_PATH } from 'components/main/constants';
import LicenseHeader from '../LicenseHeader';

enum LicenseActivationMethod {
  Online = 0,
  Manual = 1,
}

const alertError = (message: string) =>
  showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, message);

const ManualHintLabel: React.FC = () => (
  <div id="license-activation-manual-hint-label">
    Manually load a license activation file (.XML) from your local drive or external file.
    (Contact <b>support@formulatrix.com</b> for assistance)
  </div>
);

const OnlineHintLabel: React.FC<{
  clientIdChange(val: string);
  clientId: string;
}> = ({ clientId, clientIdChange }) => (
  <div id="license-activation-online-hint-label-wrapper">
    <div id="license-activation-online-hint-label-message" className="mb-2">
      Input the client ID that you have received via email and tap ACTIVATE to activate
      your license. When you have a trouble with activation License, please contact{' '}
      <b>support@formulatrix.com</b> for assistance.
    </div>
    <FmlxTextBox
      id="license-activation-online-hint-label-text-box-input-client-id"
      size={FmlxTextInput.Size.SMALL}
      placeholder="Input Client ID"
      onChange={({ value }) => clientIdChange(value)}
      value={clientId}
    />
  </div>
);

const licenseClient: LicenseClient = ModelFactory(LicenseClient, false, true);

const LicenseActivation: React.FC<{ closeModal(): any }> = ({ closeModal }) => {
  const [option, setOption] = useState<LicenseActivationMethod>(
    LicenseActivationMethod.Online,
  );
  const [licenseIsActive, setLicenseActive] = useState<boolean>(false);
  const [clientId, setClientId] = useState<string>('');
  const [showActiveLicenseConfirmation, setShowActiveLicenseConfirmation] =
    useState<boolean>(false);
  const inputFile = useRef<HTMLInputElement>(null);
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const selectionRef = React.useRef<HTMLDivElement>();
  const logout = useLogoutUser();

  const onClientIdChange = (val: string) => {
    if (!showActiveLicenseConfirmation) {
      setClientId(val);
    }
  };

  const relogin = () => {
    showSnackBar('Activate License Success');
    closeModal();
    setTimeout(() => logout(DASHBOARD_PATH), 500);
  };

  const hintLabels = [
    <OnlineHintLabel
      key="online-method-hint"
      clientIdChange={onClientIdChange}
      clientId={clientId}
    />,
    <ManualHintLabel key="manual-method-hint" />,
  ];

  const optionChange = ({ index }) => {
    index = parseInt(index, 10);
    setOption(
      index === 0 ? LicenseActivationMethod.Online : LicenseActivationMethod.Manual,
    );
    setClientId('');
  };
  const activateManual = () => {
    inputFile.current?.click();
  };
  const activateOnline = async () => {
    setIsActivating(true);
    let success = false;
    try {
      const response = await licenseClient.activateLicenseOnline(clientId);
      // TODO: fix the response type
      const result = JSON.parse(await response.data.text());

      if (typeof result === 'object' && result && result[0] == 'active') {
        success = true;
      } else {
        alertError('Activate License Failed');
      }
    } finally {
      setIsActivating(false);
    }
    if (success) {
      relogin();
    }
  };
  const activateButtonClicked = async () => {
    if (option === LicenseActivationMethod.Online) {
      if (licenseIsActive) {
        setShowActiveLicenseConfirmation(true);
      } else {
        await activateOnline();
      }
    } else if (option === LicenseActivationMethod.Manual) {
      await activateManual();
    }
  };
  const getLicenseIsActive = async () => {
    const licDetails = await licenseClient.getLicenseDetail(false);
    if (licDetails) {
      setLicenseActive(licDetails.isActive);
    }
  };
  const fileChangedHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      const content = reader.result;
      if (typeof content !== 'string') {
        console.error('selected file content type is not string');
        alertError('Invalid file format. Please select a valid license activation file.');
        return;
      }
      submitManualActivation(content);
    };

    try {
      reader.readAsText(file);
    } catch (error) {
      console.error('failed to read license file', error);
      alertError('Invalid file format. Please select a valid license activation file.');
    }
  };

  async function submitManualActivation(content: string) {
    setIsActivating(true);
    let success = false;
    try {
      const response = await licenseClient.activateLicenseManual(content);
      // TODO: fix the response type
      const result = JSON.parse(await response.data.text());

      if (typeof result === 'object' && result && result[0] != 'error') {
        success = true;
      } else {
        alertError('Activate License Failed');
      }
    } finally {
      setIsActivating(false);
    }
    if (success) {
      relogin();
    }
  }

  useEffect(() => {
    getLicenseIsActive();
  }, []);

  useEffect(() => {
    // HACK: disable all radio buttons
    const inputs = selectionRef.current?.getElementsByTagName('input');
    if (inputs) {
      for (let i = 0; i < inputs.length; i++) {
        if (inputs[i].type === 'radio') {
          inputs[i].disabled = showActiveLicenseConfirmation;
        }
      }
    }
  }, [showActiveLicenseConfirmation]);

  return (
    <FmlxModal
      id="license-activation-license-modal"
      open
      customHeader={<LicenseHeader />}
      customFooter={
        <Footer
          isActivating={isActivating}
          showActiveLicenseConfirmation={showActiveLicenseConfirmation}
          clientId={clientId}
          option={option}
          activateButtonClicked={activateButtonClicked}
          closeModal={closeModal}
          setShowActiveLicenseConfirmation={setShowActiveLicenseConfirmation}
          activateOnline={activateOnline}
        />
      }
      primaryButton={{ show: false } as any}
      tertiaryButton={{ show: false } as any}
      secondaryButton={{ show: false } as any}
    >
      <h5 id="license-activation-license-modal-label" className="label-primary">
        License Activation
      </h5>
      <hr />
      {showActiveLicenseConfirmation && <LicenseWarning />}
      <div id="license-activation-select-wrapper" className="px-5">
        <div id="license-activation-select-label" className="license-select-label">
          Please select the license activation method:
        </div>
        <div id="license-activation-radio-button-option" ref={selectionRef}>
          <FmlxRadioButton
            id="license-activation-method-selection"
            selectedIndex={option}
            size={FmlxRadioButton.Size.LARGE}
            items={[
              {
                type: 'information',
                content: 'Online Activation',
                hint:
                  option === LicenseActivationMethod.Online ? (hintLabels[0] as any) : '',
                disabled: false,
              },
              {
                type: 'information',
                content: 'Manual Activation',
                hint:
                  option === LicenseActivationMethod.Manual ? (hintLabels[1] as any) : '',
                disabled: false,
              },
            ]}
            onChange={optionChange}
          />
          <input
            id="license-activation-input-file"
            type="file"
            ref={inputFile}
            accept=".xml"
            className="d-none"
            onInput={fileChangedHandler}
          />
        </div>
      </div>
      <hr />
    </FmlxModal>
  );
};

const LicenseWarning: React.FC = () => (
  <div id="license-activation-license-warning-banner" className="mb-2">
    <FmlxBanner
      id="license-activation-warning-content"
      variant={FmlxBanner.Variant.WARNING}
      title="You have already an active license. Requesting it again will renew your license"
      subtitle=""
      hideCloseButton
      show
    />
  </div>
);

const Footer: React.FC<{
  isActivating: boolean;
  activateButtonClicked(): any;
  closeModal(): any;
  showActiveLicenseConfirmation: boolean;
  setShowActiveLicenseConfirmation(show: boolean): any;
  activateOnline(): any;
  option: LicenseActivationMethod;
  clientId: string;
}> = ({
  isActivating,
  activateButtonClicked,
  closeModal,
  showActiveLicenseConfirmation,
  setShowActiveLicenseConfirmation,
  activateOnline,
  option,
  clientId,
}) => {
  return showActiveLicenseConfirmation ? (
    <div
      id="license-activation-footer-wrapper-confirm"
      className="mr-5 pr-1 mb-5 content-right-align"
    >
      <FmlxButton
        id="license-activation-cancel-button"
        type={FmlxButton.Type.PRIMARY}
        variant={FmlxButton.Variant.OUTLINE}
        label="CANCEL"
        disabled={isActivating}
        onClick={() => setShowActiveLicenseConfirmation(false)}
      />
      <FmlxButton
        id="license-activation-coninue-button"
        type={FmlxButton.Type.PRIMARY}
        label="CONTINUE"
        disabled={isActivating}
        onClick={activateOnline}
      />
    </div>
  ) : (
    <div
      id="license-activation-footer-wrapper-request"
      className="mr-5 pr-1 mb-5 content-right-align"
    >
      <FmlxButton
        id="license-activation-back-button"
        type={FmlxButton.Type.PRIMARY}
        variant={FmlxButton.Variant.OUTLINE}
        label="BACK"
        onClick={closeModal}
        disabled={isActivating}
      />
      <FmlxButton
        id="license-activation-active-button"
        type={FmlxButton.Type.PRIMARY}
        disabled={
          isActivating || (option === LicenseActivationMethod.Online && !clientId?.trim())
        }
        label="ACTIVATE"
        onClick={activateButtonClicked}
      />
    </div>
  );
};

export default LicenseActivation;
